import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import GridItem from 'components/UI/Grid/GridItem';
import GridContainer from 'components/UI/Grid/GridContainer';
import Button from 'components/UI/CustomButtons/Button';
import withStyles from '@material-ui/core/styles/withStyles';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { TableLoader } from 'components/common/TableLoader';
import ReactTable from 'react-table';
import { formatRoute } from 'react-router-named-routes';
import qs from 'helpers/queryStrings';
import { PAYMENT, USER } from 'routes/paths';
import Moment from 'react-moment';
import { paymentStatus, orderTypes } from 'config';
import Paginator from 'components/common/Paginator';
import ContentLoader from 'react-content-loader';
import RouterContext from 'helpers/routerContext';
import { defaultFilters } from 'components/Payment/List';
import FilterIco from '@material-ui/icons/FilterList';
import Filter from './Filter';

class PaymentList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      showFilters: false,
    };
  }

  /**
   * @description
   * Redirect to a route.
   *
   * @param {string} route - Route where navigate to.
   * @param {Object} params - Params to add.
   */
  handleNavigation = (route, params = {}) => {
    const history = this.context;

    const { appliedFilters } = this.props;

    const urlString = qs.stringify({
      ...appliedFilters,
    });

    history.push({
      pathname: formatRoute(route, { ...params }),
      search: urlString,
    });
  };

  /**
   * @description
   * Description of ReactTable columns.
   */
  renderColumns = () => {
    const { classes, t } = this.props;
    return [
      {
        Header: null,
        accessor: 'id',
        sortable: false,
        filterable: false,
        maxWidth: 60,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.externalId')}
          </span>
        ),
        accessor: 'externalId',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.amount')}
          </span>
        ),
        accessor: 'amount',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.status')}
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.type')}
          </span>
        ),
        accessor: 'type',
        sortable: false,
        filterable: false,
        maxWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.product')}
          </span>
        ),
        accessor: 'product',
        sortable: false,
        filterable: false,
        maxWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.date')}
          </span>
        ),
        accessor: 'date',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.user')}
          </span>
        ),
        accessor: 'user',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('payment.list.headers.agency')}
          </span>
        ),
        accessor: 'agency',
        sortable: false,
        filterable: false,
      },
      {
        Header: null,
        sortable: false,
        filterable: false,
        maxWidth: 20,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All payments from api.
   * @returns {*} - Rows to reactable.
   */
  renderRows = edges => {
    const { classes, t, appliedFilters } = this.props;

    const renderStatus = status => {
      switch (status) {
        case paymentStatus.PENDING:
          return (
            <h6 className={classes.pendingTag}>
              {t('payment.list.status.pending')}
            </h6>
          );
        case paymentStatus.PAYED:
          return (
            <h6 className={classes.runningTag}>
              {t('payment.list.status.payed')}
            </h6>
          );
        case paymentStatus.REFUNDED:
          return (
            <h6 className={classes.runningTag}>
              {t('payment.list.status.refund')}
            </h6>
          );
        case paymentStatus.PARTIAL_REFUNDED:
          return (
            <h6 className={classes.runningTag}>
              {t('payment.list.status.refund')}
            </h6>
          );
        case paymentStatus.TOTAL_REFUNDED:
          return (
            <h6 className={classes.runningTag}>
              {t('payment.list.status.refund')}
            </h6>
          );
        case paymentStatus.CANCELLED:
          return (
            <h6 className={classes.deletedTag}>
              {t('payment.list.status.canceled')}
            </h6>
          );
        case paymentStatus.FAILED:
          return (
            <h6 className={classes.deletedTag}>
              {t('payment.list.status.failed')}
            </h6>
          );
        case paymentStatus.PENDING_REFUND:
          return (
            <h6 className={classes.pendingTag}>
              {t('payment.list.status.pending_refund')}
            </h6>
          );
        case paymentStatus.FAILED_REFUND:
          return (
            <h6 className={classes.deletedTag}>
              {t('payment.list.status.failed_refund')}
            </h6>
          );
        case paymentStatus.UNRESOLVED:
          return (
            <h6 className={classes.scheduledTag}>
              {t('payment.list.status.unresolved')}
            </h6>
          );
        default:
          return (
            <h6 className={classes.unknownTag}>
              {t('payment.list.status.unknown')}
            </h6>
          );
      }
    };

    return edges.map(({ node }) => ({
      id: <div style={{ textAlign: 'center', fontWeight: 500 }}>{node.id}</div>,
      externalId: (
        <a
          data-testid={node.id}
          href={`${formatRoute(PAYMENT.DETAILS, { id: node.id })}`}
          onClick={e => {
            e.preventDefault();
            this.handleNavigation(PAYMENT.DETAILS, {
              id: node.id,
            });
          }}
          style={{
            color: 'black',
            fontWeight: '500',
          }}
        >
          {node.platformPaymentId}
        </a>
      ),
      amount: (
        <a
          href={`${formatRoute(PAYMENT.DETAILS, {
            id: node.id,
          })}?${qs.stringify({ ...appliedFilters })}`}
          onClick={e => {
            e.preventDefault();
            this.handleNavigation(PAYMENT.DETAILS, {
              id: node.id,
            });
          }}
          className={`${classes.titleBtn} title`}
        >
          {node.amount} {node.currency}
        </a>
      ),
      status: renderStatus(node.status),
      type: (
        <p style={{ fontWeight: 500, marginBottom: 0 }}>{node.order.type}</p>
      ),
      product: (
        <p style={{ fontWeight: 500, marginBottom: 0 }}>
          {node.order.orderType}
        </p>
      ),
      date: (
        <div
          style={{
            fontWeight: 400,
          }}
        >
          {node.createdOn && (
            <p>
              <Moment date={node.createdOn} format="llll" fromNow />
            </p>
          )}
          {node.updatedOn && (
            <p
              style={{
                marginBottom: 0,
              }}
            >
              <Moment date={node.updatedOn} format="llll" fromNow />
            </p>
          )}
        </div>
      ),
      agency: node.order.orderType === orderTypes.CREDIT && (
        <a
          data-testid={node.order.agency.id}
          href={formatRoute(USER.DETAILS, { id: node.order.agency.id })}
          onClick={e => {
            e.preventDefault();
            this.handleNavigation(USER.DETAILS, {
              id: node.order.agency.id,
            });
          }}
          style={{
            fontWeight: 500,
          }}
        >
          <p
            style={{
              marginTop: 8,
            }}
          >
            {node.order.agency.fullname}
          </p>
          <p>{node.order.agency.email}</p>
          <p>{node.order.agency.phone && node.order.agency.phone}</p>
        </a>
      ),
      user: node.user && (
        <div
          style={{
            color: 'black',
            fontWeight: '500',
          }}
        >
          <p
            style={{
              marginTop: 8,
            }}
          >
            {node.user.fullname}
          </p>
          <p>{node.user.email}</p>
        </div>
      ),
    }));
  };

  /**
   * @description
   * Handle show collapsed/full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  render() {
    const {
      data,
      classes,
      t,
      loadingData,
      error,
      numberOfRowsSelected,
      onChangePage,
      onChangeNumberOfRows,
      paginationDirection,
      appliedFilters,
      onFilter,
      onRestartFilters,
    } = this.props;

    if (error) {
      return (
        <div>
          <h3>{t('errors.view')}</h3>
          <p>{error.message}</p>
        </div>
      );
    }
    const { showFilters } = this.state;
    const edges = loadingData ? [] : data.payments.edges;

    const columns = this.renderColumns(edges);
    const rows = this.renderRows(edges);

    return (
      <>
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <h3 className={classes.title}>{t('payment.list.title')}</h3>
          </GridItem>
        </GridContainer>

        <div className={classes.contentContainer}>
          <GridContainer justify="space-between" alignItems="center">
            <GridItem xs={12} sm={3} md={5} lg={4}>
              <Button
                color="info"
                round
                simple={
                  !showFilters &&
                  JSON.stringify(appliedFilters) ===
                    JSON.stringify(defaultFilters)
                }
                onClick={this.handleShowFilters}
              >
                <FilterIco />
                <span>
                  {JSON.stringify(appliedFilters) ===
                  JSON.stringify(defaultFilters)
                    ? t('user.list.filter')
                    : t('user.list.filtering')}
                </span>
              </Button>
            </GridItem>
            <GridItem xs={12} sm={9} md={5} lg={4}>
              {!loadingData ? (
                <Paginator
                  pageInfo={data.payments.pageInfo}
                  changePage={onChangePage}
                  changeNumberOfRows={onChangeNumberOfRows}
                  numberOfRowsSelected={numberOfRowsSelected}
                  paginationDirection={paginationDirection}
                />
              ) : (
                <ContentLoader height={67} width={327} speed={2}>
                  <path
                    // eslint-disable-next-line max-len
                    d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                    fill="#D8D8D8"
                  />
                </ContentLoader>
              )}
            </GridItem>
          </GridContainer>

          <div
            style={{
              maxHeight: showFilters ? 900 : 0,
              overflow: 'hidden',
              transition: 'all .2s ease',
            }}
          >
            <Filter
              appliedFilters={appliedFilters}
              onShowFilters={this.handleShowFilters}
              onFilter={onFilter}
              onRestartFilters={onRestartFilters}
            />
          </div>

          <GridContainer>
            <GridItem xs={12}>
              {loadingData ? (
                <TableLoader />
              ) : (
                <ReactTable
                  data={rows}
                  filterable
                  columns={columns}
                  loading={loadingData}
                  pageSize={
                    edges.length < numberOfRowsSelected
                      ? edges.length
                      : numberOfRowsSelected
                  }
                  showPagination={false}
                  className={['-striped', '-highlight', classes.tbl].join(' ')}
                  noDataText={t('common.no_payments')}
                  minRows={edges.length < 1 ? 8 : edges.length}
                />
              )}
            </GridItem>
          </GridContainer>
        </div>
      </>
    );
  }
}

/**
 * Payment list prop types
 * @param {object} data - result query for listed items
 * @param {object} classes - classnames from HOC for styles
 * @param {object} t - prop from HOC with string translations
 * @param {object} appliedFilters - prop from HOC with query string of filters
 * @param {object} data - prop from HOC with query result
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param {boolean} loadingData - when whe are waiting for data prop
 * @param {string} paginationDirection - pagination direction
 */
PaymentList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  loadingData: PropTypes.bool.isRequired,
  data: PropTypes.shape({}).isRequired,
  appliedFilters: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeNumberOfRows: PropTypes.func.isRequired,
  paginationDirection: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  onRestartFilters: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
const PaymentListWrapper = props => <PaymentList {...props} />;

export default withTranslation()(
  withStyles({ ...extendedTablesStyle, ...extendedFormsStyle })(
    PaymentListWrapper,
  ),
);
